import { Card, Grid, makeStyles, Typography } from '@material-ui/core'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import { useState } from 'react'
import {
  ArrayField,
  Button,
  Datagrid,
  Error,
  FunctionField,
  Labeled,
  Loading,
  maxLength,
  NumberField,
  ReferenceField,
  required,
  Tab,
  TabbedShowLayout,
  TextField,
  TextInput,
  useGetOne,
} from 'react-admin'
import { Redirect } from 'react-router-dom'
import { usePageTitle } from 'shared/components/PageTitle/PageTitle'

import { LoadStop } from 'models/jobs'
import { addOrEditTrailerNumber } from '../../api/jobs'
import {
  AddOrEditTextField,
  CustomBooleanField,
  CustomLinkifiedField,
  LocalizedStopDateTimeField,
} from '../Fields'
import NotesList from '../Notes/NotesList'

const useLoadStyles = makeStyles(() => ({
  clickToCopyButton: {
    color: '#0000ee',
    padding: 0,
    '& span': {
      padding: 0,
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}))

type OnelineAddressProps = {
  record: LoadStop
  label: string
  props?: any
}

export const OnelineAddress = ({ record, ...props }: OnelineAddressProps) => {
  let address1 = record.location_address1 ? `${record.location_address1}, ` : ''
  let address2 = record.location_address2 ? `${record.location_address2}, ` : ''
  let fullAddress = `${address1}${address2}${record.location_city}, ${record.location_state} ${record.location_zip}`
  return (
    <Typography component="span" variant="body2" {...props}>
      {fullAddress}
    </Typography>
  )
}

type LoadStopsProps = {
  record?: any
  source: string
  changeTrailerNumber?: boolean
}

export const LoadStops = ({ record, source, changeTrailerNumber = false }: LoadStopsProps) => {
  return (
    <ArrayField record={record} source={source} label="Stops">
      <Datagrid>
        <NumberField source="sequence_number" />
        <TextField source="type" />
        <TextField source="type_detail" />
        <TextField source="loading_type" />
        <LocalizedStopDateTimeField source="date_start" label="Appointment window start" />
        <LocalizedStopDateTimeField source="date_end" label="Appointment window end" />
        <TextField source="location_name" />
        <OnelineAddress record={record} label="Location address" />
        <FunctionField
          label="Trailer number"
          render={(stop: any) => {
            if (changeTrailerNumber) {
              const payload = { stop_seq_num: stop.sequence_number, trailer_number: stop.trailer_number }
              return (
                <AddOrEditTextField
                  initialPayload={payload}
                  label="Trailer number"
                  source="trailer_number"
                  api={addOrEditTrailerNumber}
                  id={record.load ? record.load.id : record.id}
                  initialValue={stop.trailer_number}
                />
              )
            } else {
              return <TextField source="trailer_number" />
            }
          }}
        />
        <TextField source="notes" />
      </Datagrid>
    </ArrayField>
  )
}

type CloneLoadButtonProps = {
  record: any
  showWarning?: boolean
  disabled?: boolean
}

export const CloneLoadButton = ({ record, showWarning, disabled }: CloneLoadButtonProps) => {
  const [redirect, setRedirect] = useState(false)
  const [isWarningOpen, setWarningOpen] = useState(false)

  return redirect ? (
    <Redirect
      to={{
        pathname: '/loads/create',
        state: {
          load: record,
        },
      }}
      push={true}
    />
  ) : (
    <>
      <Button
        variant="contained"
        color="primary"
        label="Clone load"
        disabled={disabled}
        onClick={() => (showWarning ? setWarningOpen(true) : setRedirect(true))}
      />
      <Dialog open={isWarningOpen} onClose={() => setWarningOpen(false)}>
        <DialogTitle>Clone Load with Outstanding Invoice?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This load has an outstanding invoice. Make sure to review the invoice before cloning this load.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRedirect(true)} label="Continue" />
          <Button onClick={() => setWarningOpen(false)} label="Cancel" variant="contained" />
        </DialogActions>
      </Dialog>
    </>
  )
}

export const BookContactedLoadButton = ({ record, basePath }: any) => {
  const [redirect, setRedirect] = useState(false)
  const { data, loading, error } = useGetOne('users', record.created_by.id)
  if (loading) {
    return <Loading />
  }
  if (error) {
    return <Error error={error} />
  }

  return redirect ? (
    <Redirect
      to={{
        pathname: `${basePath}/${record.id}/book`,
        state: {
          contactedLoadTeamId: data?.team.id,
        },
      }}
      push={true}
    />
  ) : (
    <Button variant="contained" color="primary" label="Book load" onClick={() => setRedirect(true)} />
  )
}

export const LoadShowLayout = ({ isContactedLoad, record }: any) => {
  const load = isContactedLoad ? record.load : record
  const classes = useLoadStyles()

  usePageTitle(`Load ${load?.source_human_id || ''}`)

  return (
    <Grid container direction="column" spacing={3} alignItems="baseline" justifyContent="flex-start">
      <Grid item container direction="row" alignItems="stretch" justifyContent="flex-end" spacing={0}>
        <Grid item container direction="column" xs={6}>
          <NotesList parentId={record.id} parentName="loads" buttonClass={classes.clickToCopyButton} />
        </Grid>
      </Grid>
      <Grid item>
        <Card style={{ marginTop: '20px', paddingBottom: '20px' }}>
          <TabbedShowLayout record={load}>
            <Tab label="Load information">
              <TextField source="id" />
              <TextField source="source" />
              <Labeled label="Broker">
                <ReferenceField
                  basePath="/brokers"
                  record={record}
                  source="broker.id"
                  reference="brokers"
                  link="show"
                >
                  <TextField source="name" />
                </ReferenceField>
              </Labeled>
              {record?.job_to_recover && (
                <Labeled label="Job to recover">
                  <ReferenceField
                    basePath="/jobs"
                    record={record}
                    source="job_to_recover.id"
                    reference="jobs"
                    link="show"
                  >
                    <TextField source="human_id" />
                  </ReferenceField>
                </Labeled>
              )}
              <TextField source="source_id" label="Source Id" />
              <TextField source="source_human_id" label="Broker load ID" />
              <TextField source="contact_name" />
              <TextField source="contact_phone" />
              <TextField source="contact_email" />
              <TextField source="company_name" />
              <CustomLinkifiedField source="internal_notes" label="Internal notes" record={record} />
              <LoadStops source="stops" record={record} />
              <CustomBooleanField source="is_load_out_trailer" label="Load out trailer" />
              <CustomBooleanField source="standard_book" />
              <CustomBooleanField source="instant_book" />
              <CustomBooleanField source="bid_book" />
              <CustomBooleanField source="has_auto_bid" />
              <TextField source="trip_rate" />
              <TextField source="estimated_rate" label="CT Estimated Rate" />
              <TextField source="estimated_rate_min" label="CT Estimated Rate Min" />
              <TextField source="estimated_rate_max" label="CT Estimated Rate Max" />
              <NumberField source="trip_distance_mi" />
            </Tab>
            <Tab label="Equipment">
              <TextField source="equipment" />
              <TextField source="equipment_detail" />
              <TextField source="truck_full_load" />
              <NumberField source="truck_weight_lb" />
              <NumberField source="truck_length_ft" />
            </Tab>
            <Tab label="Misc" path="misc">
              <NumberField source="trip_stops" />
              <TextField source="date_booked" />
              <TextField source="estimated_revenue_per_hour" />
              <TextField source="comment" />
              <TextField source="origin_city" />
              <TextField source="origin_state" />
              <TextField source="origin_zip" />
              <TextField source="origin_deadhead_mi" />
              <TextField source="dest_city" />
              <TextField source="dest_state" />
              <TextField source="dest_zip" />
              <TextField source="dest_deadhead_mi" />
              <TextField source="content_hash" />
              <TextField source="posted_date" />
              <NumberField source="total_deadhead_mi" />
              <CustomBooleanField source="is_team_load" />
            </Tab>
          </TabbedShowLayout>
        </Card>
      </Grid>
    </Grid>
  )
}

// Returns a TextInput for a load stop's reference_code field.
// referenceCodeSource - source for the input
// isRequired - indicates whether or not to mark the field as required
export const LoadStopReferenceCodeInput = ({ referenceCodeSource, isRequired }: any) => {
  return (
    <TextInput
      source={referenceCodeSource}
      label="PO, PU, Shipment or BOL number"
      helperText="These numbers can be found on a ratecon or BOL"
      style={{ width: 400 }}
      validate={(() => {
        if (isRequired) {
          return [maxLength(250), required()]
        } else {
          return [maxLength(250)]
        }
      })()}
    />
  )
}
